import React from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { motion } from 'framer-motion';
import { getProductPath, Product } from '../data/products';
import TextField from '@mui/material/TextField';

interface ProductGridProps {
  products: Product[];
  categoryName: string;
  addToCart?: (product: Product, quantity: number) => void;
}

const ProductGrid: React.FC<ProductGridProps> = ({ products, categoryName, addToCart }) => {
  const navigate = useNavigate();
  const [qtys, setQtys] = React.useState<{ [id: string]: number }>({});

  return (
    <Box sx={{ width: '100%', maxWidth: 1200 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
      >
        <Typography variant="h4" fontWeight="bold" sx={{ mb: 3, textAlign: 'center' }}>
          Productos de {categoryName}
        </Typography>
      </motion.div>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
          gap: 3,
        }}
      >
        {products.map((product, index) => (
          <motion.div
            key={product.id}
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{
              duration: 0.6,
              delay: index * 0.1,
              ease: 'easeOut',
            }}
            whileHover={{
              scale: 1.03,
              transition: { duration: 0.2 },
            }}
          >
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                overflow: 'visible',
              }}
            >
              <CardActionArea
                sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                onClick={() => navigate(getProductPath(product.id))}
              >
                <CardMedia
                  component="img"
                  height="250"
                  image={product.image}
                  alt={product.name}
                  sx={{
                    objectFit: 'contain',
                    bgcolor: '#f5f5f5',
                    p: 1,
                  }}
                />
                <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography gutterBottom variant="h6" component="div" fontWeight="bold" sx={{ mb: 1 }}>
                    {product.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flex: 1 }}>
                    {product.description}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.7, mb: 2 }}>
                    {product.features.slice(0, 2).map((feature, idx) => (
                      <Chip
                        key={idx}
                        label={feature}
                        size="small"
                        sx={{
                          fontSize: '0.7rem',
                          bgcolor: '#e8f5e8',
                          color: '#2e7d32',
                        }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </CardActionArea>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', p: 2, pt: 0 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                  <TextField
                    type="number"
                    size="small"
                    label="Cantidad"
                    value={qtys[product.id] || 1}
                    onChange={(e) =>
                      setQtys((q) => ({ ...q, [product.id]: Math.max(1, parseInt(e.target.value) || 1) }))
                    }
                    inputProps={{ min: 1, style: { width: 50, textAlign: 'center' } }}
                    sx={{ mb: 1 }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  {!product.inStock && (
                    <Chip
                      label="Agotado"
                      size="small"
                      sx={{
                        bgcolor: '#ffebee',
                        color: '#c62828',
                        fontSize: '0.7rem',
                      }}
                    />
                  )}
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<ShoppingCartIcon />}
                    disabled={!product.inStock}
                    sx={{
                      bgcolor: product.inStock ? '#4caf50' : '#ccc',
                      '&:hover': {
                        bgcolor: product.inStock ? '#388e3c' : '#ccc',
                      },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      addToCart && addToCart(product, qtys[product.id] || 1);
                      setQtys((q) => ({ ...q, [product.id]: 1 }));
                    }}
                  >
                    {product.inStock ? 'Agregar' : 'Agotado'}
                  </Button>
                </Box>
              </Box>
            </Card>
          </motion.div>
        ))}
      </Box>
    </Box>
  );
};

export default ProductGrid;
